// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import useIsPackageAvailableFrom from '@hooks/queries/settings/useIsPackageAvailableFrom';
import React, { useLayoutEffect } from 'react';
import { Typography } from '@mui/material';
import FormDialog from '../../components/Dialog/FormDialog';
import TextInput from '../../components/Form/TextInput';
import { useParties } from '../../contexts/PartiesContext';
import { useCreateCommercialAgreement } from '../../hooks/mutations/commercialAgreements/useCreateCommercialAgreementOffer';
import useMutate from '../../hooks/mutations/useMutate';
import useDsoParty from '../../hooks/queries/cn/useDsoParty';
import { isPositiveDecimal } from '../../utils/isPostiveDecimal';
import {
  ERR_CREATE_COMMERCIAL_AGREEMENT,
  SUC_CREATE_COMMERCIAL_AGREEMENT,
} from '../../utils/strings';
import { DialogProps } from './DialogProps';

interface IForm {
  utilityFees: string;
  user: string;
  feeReceiver: string;
  ratePerDay?: string;
  billingPeriodMinutes?: string;
  rewardReceiver?: string;
}

export const CreateCommercialAgreementDialog: React.FC<DialogProps> = ({ open, onClose }) => {
  const { party, operator } = useParties();
  const enableRewardReceiverConfig = useIsPackageAvailableFrom(
    'utility-commercials-v0',
    [party, operator],
    '0.3.0',
  );
  const dsoParty = useDsoParty()?.data;

  const [form, setForm] = React.useState({} as IForm);

  const isBaseFeeInitialized = !!(form.ratePerDay || form.billingPeriodMinutes);

  const [utilityFeesValidationMsg, setUtilityFeesValidationMsg] = React.useState('');
  const [ratePerDayValMsg, setRatePerDayValMsg] = React.useState('');
  const [billingPeriodMinutesValMsg, setBillingPeriodMinutesValMsg] = React.useState('');

  const clearBaseFeeValidationMessages = () => {
    setRatePerDayValMsg('');
    setBillingPeriodMinutesValMsg('');
  };

  useLayoutEffect(() => {
    if (isBaseFeeInitialized) return;
    clearBaseFeeValidationMessages();
  }, [isBaseFeeInitialized]);

  const getUtilityFeeValidationMsg = (amount: string): string => {
    if (!amount) {
      return 'Amount must not be empty';
    }

    if (!isPositiveDecimal(amount)) {
      return 'Amount must not be negative';
    }

    return '';
  };

  const hasValidationMsgs = !!(
    utilityFeesValidationMsg ||
    billingPeriodMinutesValMsg ||
    ratePerDayValMsg
  );

  const isBaseFeeValid = isBaseFeeInitialized ? form.ratePerDay && form.billingPeriodMinutes : true;
  const isFormValid = form.feeReceiver && form.user && form.utilityFees && isBaseFeeValid;

  const isSubmitDisabled = !isFormValid || hasValidationMsgs;

  const createCommercialAgreement = useCreateCommercialAgreement();

  const handleClose = React.useCallback(() => {
    setForm({} as IForm);
    onClose();
    setUtilityFeesValidationMsg('');
  }, [onClose]);

  const mutate = useMutate();

  const handleSubmit = React.useCallback(async () => {
    const payload = {
      user: form.user,
      dso: dsoParty || '',
      operator,
      feeReceiver: form.feeReceiver,
      utilityFees: {
        credentialBillingFeeUsd: form.utilityFees,
        baseFee:
          form.billingPeriodMinutes && form.ratePerDay
            ? {
                billingPeriodMinutes: form.billingPeriodMinutes,
                feePerDayUsd: { rate: form.ratePerDay },
              }
            : null,
      },
      rewardReceiver: form.rewardReceiver ? form.rewardReceiver : null,
    };
    await mutate(
      createCommercialAgreement,
      payload,
      SUC_CREATE_COMMERCIAL_AGREEMENT,
      ERR_CREATE_COMMERCIAL_AGREEMENT,
    );
    handleClose();
  }, [handleClose, mutate, form, createCommercialAgreement, operator, dsoParty]);

  return (
    <FormDialog
      open={open}
      title="Create Commercial Agreement Offer"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Create"
      isSubmitDisabled={isSubmitDisabled}
    >
      <TextInput
        label="User"
        enableTopLabel
        value={form.user}
        setValue={(value) => setForm({ ...form, user: value as string })}
      />
      <TextInput
        enableTopLabel
        label="Fee Receiver"
        value={form.feeReceiver}
        setValue={(value) => setForm({ ...form, feeReceiver: value as string })}
      />
      {enableRewardReceiverConfig.data && (
        <TextInput
          enableTopLabel
          label="Reward Receiver"
          value={form.rewardReceiver || ''}
          setValue={(value) => setForm({ ...form, rewardReceiver: value as string })}
        />
      )}
      <TextInput
        enableTopLabel
        label="Credential Billing Fee (USD)"
        aria-label="Credential Billing Fee (USD)"
        value={form.utilityFees}
        type="number"
        getValidationMessage={getUtilityFeeValidationMsg}
        setValidationMessage={setUtilityFeesValidationMsg}
        validationMessage={utilityFeesValidationMsg}
        setValue={(value) => setForm({ ...form, utilityFees: value as string })}
      />
      <Typography sx={{ mb: 1, mt: 2 }}>Base Fee</Typography>
      <TextInput
        enableTopLabel
        label="Fee per Day USD"
        aria-label="Fee per Day USD"
        value={form.ratePerDay || ''}
        getValidationMessage={getUtilityFeeValidationMsg}
        setValidationMessage={setRatePerDayValMsg}
        validationMessage={ratePerDayValMsg}
        type="number"
        setValue={(value) => setForm({ ...form, ratePerDay: value as string })}
      />
      <TextInput
        enableTopLabel
        label="Billing Period (minutes)"
        aria-label="Billing Period (minutes)"
        value={form.billingPeriodMinutes || ''}
        getValidationMessage={getUtilityFeeValidationMsg}
        setValidationMessage={setBillingPeriodMinutesValMsg}
        validationMessage={billingPeriodMinutesValMsg}
        type="number"
        setValue={(value) => setForm({ ...form, billingPeriodMinutes: value as string })}
      />
    </FormDialog>
  );
};
